import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  fetchRoadmapSegmentConversation,
  sendRoadmapSegmentConversationMessage,
} from "../../api/roadmapClient";
import type { RoadmapSegmentConversationMessage } from "../../types/roadmapConversations";

interface SegmentConversationPanelProps {
  segmentId: string;
}

export function SegmentConversationPanel({ segmentId }: SegmentConversationPanelProps): JSX.Element {
  const [messages, setMessages] = useState<RoadmapSegmentConversationMessage[]>([]);
  const [input, setInput] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
    void loadConversation();
  }, [segmentId]);

  const orderedMessages = useMemo(() => {
    return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [messages]);

  const loadConversation = async () => {
    if (!segmentId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchRoadmapSegmentConversation(segmentId);
      setMessages(result.messages);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load conversation";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!segmentId || !input.trim()) return;

    setIsSending(true);
    setError(null);

    try {
      const result = await sendRoadmapSegmentConversationMessage(segmentId, input.trim());
      setMessages(result.messages.length ? result.messages : [result.message]);
      setInput("");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to send message";
      // eslint-disable-next-line no-console
      console.error("Segment conversation send failed", err);
      setError(message);
    } finally {
      setIsSending(false);
    }
  };

  useEffect(() => {
    if (!messageListRef.current) return;
    messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
  }, [orderedMessages]);

  return (
    <div className="rounded-lg border border-border bg-card shadow-soft">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div>
          <div className="text-sm font-semibold text-foreground">Assistant segmentaire</div>
          <div className="text-xs text-muted-foreground">
            Analyse la cohérence du segment et l'alignement avec les use cases liés.
          </div>
        </div>
        <button type="button" className="button button-ghost button-xs" onClick={() => void loadConversation()}>
          {isLoading ? "Chargement…" : "Rafraîchir"}
        </button>
      </div>

      <div ref={messageListRef} className="max-h-64 space-y-3 overflow-y-auto px-3 py-3">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Chargement de la conversation…</p>
        ) : orderedMessages.length === 0 ? (
          <p className="text-sm text-muted-foreground">Démarrez la discussion pour challenger le segment.</p>
        ) : (
          orderedMessages.map((message) => (
            <div
              key={message.id}
              className={`flex flex-col gap-1 rounded border px-3 py-2 text-sm ${
                message.role === "assistant"
                  ? "border-indigo-100 bg-indigo-50/60 text-foreground"
                  : "border-border bg-white text-foreground"
              }`}
            >
              <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-muted-foreground">
                <span>{message.role}</span>
                <span>{new Date(message.createdAt).toLocaleString()}</span>
              </div>
              <div className="whitespace-pre-line">{message.content}</div>
            </div>
          ))
        )}
      </div>

      <form className="border-t border-border bg-muted/40 px-3 py-3" onSubmit={handleSend}>
        <label className="flex flex-col gap-2 text-xs text-muted-foreground">
          Message
          <textarea
            className="input min-h-[90px]"
            placeholder="Demandez un retour sur le segment"
            value={input}
            onChange={(event: any) => setInput(event.target.value)}
            disabled={isSending}
          />
        </label>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="submit" className="button button-primary" disabled={isSending || !input.trim()}>
            {isSending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </form>

      {error ? (
        <div className="border-t border-border bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      ) : null}
    </div>
  );
}
